import React from 'react';
import { style } from 'typestyle';
import { ComponentProps, inject, deps, observer } from '../../../lib/component';
import { rebrand } from '../../../theme/color';
import { spacing } from '../../../theme/spacing';
import { color, px } from 'csx';
import { Checkmark } from '../atoms/icons/Checkmark';
import { Clock } from '../atoms/icons/Clock';
import { QuestionCircle } from '../atoms/icons/QuestionCircle';
import { Copyright } from '../atoms/icons/Copyright';

interface LicenseTypeStatusProps extends ComponentProps {
  isClaimsManaged: boolean;
}

const styles = {
  container: style({
    backgroundColor: rebrand.neutralOnDark[10].toString(),
    padding: spacing.XSMALL,
    borderRadius: px(8),
    display: 'flex',
    alignItems: 'center',
    gap: spacing.XSMALL,
    maxWidth: px(502),
    minWidth: px(280),
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: px(24),
    marginBottom: px(24),
  }),
  content: style({
    display: 'flex',
    flexDirection: 'column',
    color: rebrand.contrast[50].toString(),
    fontSize: px(12),
    gap: spacing.XXSMALL,
  }),
  headerText: style({
    fontSize: px(14),
    fontWeight: 'bold',
  }),
  subText: style({
    display: 'flex',
    alignItems: 'center',
    $nest: {
      '& svg:nth-of-type(1)': {
        marginRight: px(4),
      },
      '& svg:nth-of-type(2)': {
        marginLeft: px(4),
      },
    },
  }),
};

export const LicenseTypeStatus: React.FC<LicenseTypeStatusProps> = inject(deps)(
  observer(
    ({
      isClaimsManaged,
      controller: {
        modal: { showAutomatedClaimsClearanceModal, showClaimsManagedModal, showLicenseValidationModal },
      },
    }) => {
      const iconColor = isClaimsManaged
        ? rebrand.semantic.warning[60].toHexString()
        : rebrand.semantic.success[70].toHexString();
      const clearanceText = isClaimsManaged ? 'Claims-managed' : 'Automated claims clearance';
      const validityText = isClaimsManaged ? 'valid for 5 years' : 'does not expire';

      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.headerText}>License Type</div>
            <div className={styles.subText}>
              <Copyright color={iconColor} size={16} />
              {clearanceText}
              <QuestionCircle
                color={rebrand.neutralOnLight[60].toString()}
                onClick={() => {
                  if (isClaimsManaged) {
                    showClaimsManagedModal();
                  } else {
                    showAutomatedClaimsClearanceModal();
                  }
                }}
                data-test-questionmark-eula-claims-managed={isClaimsManaged}
              />
            </div>
            <div className={styles.subText}>
              <Checkmark />
              License {validityText}
              {isClaimsManaged && (
                <QuestionCircle
                  color={rebrand.neutralOnLight[60].toString()}
                  onClick={() => showLicenseValidationModal()}
                  data-test-questionmark-eula-license-duration
                />
              )}
            </div>
            <div className={styles.subText}>
              <Clock size={16} />
              Use within 6 months
            </div>
          </div>
        </div>
      );
    }
  )
);
